/**
 * The audit: count both shelves, then read what the kitchen used.
 *
 * Started with a button. Two sheets open — Store and Kitchen — prefilled with
 * what the system expects, and whoever counts types what is really there, in
 * packs, half packs allowed. Lines left blank are taken as "as the system
 * says". Complete corrects the shelves and writes the report: per item what
 * came in, what went to the kitchen, what the kitchen used, what is left on
 * each shelf, with values; and at the foot, takings less stock used less
 * expenses for the same days.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPKR, formatServerTime, today } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { packs } from './ItemPicker.jsx';
import { RangeInputs } from '../reports/DashboardPage.jsx';
import { ReportExport } from '../reports/ReportExport.jsx';

const when = (s) => (s ? formatServerTime(s) : '—');
/** A day as people say it: 17 Oct 2026. */
const day = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : null);
/** "10 Oct 2026 – 17 Oct 2026", or just the one day. */
export function periodLabel(period) {
  if (!period) return '';
  const from = day(period.fromDay);
  const to = day(period.toDay) || 'today';
  if (!from) return `from the start to ${to}`;
  return from === to ? from : `${from} – ${to}`;
}

export function AuditTab({ canRun }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['store', 'audits'], queryFn: () => api.get('/store/audits') });
  const [viewing, setViewing] = useState(null);
  // The days to audit: the system suggests the day after the last audit up to today; the owner can pick others.
  const [picked, setPicked] = useState(null);
  const range = picked ?? list.data?.suggested ?? { from: today(), to: today() };
  const start = useMutation({
    mutationFn: () => api.post('/store/audits', { from: range.from, to: range.to }),
    onSuccess: () => {
      setPicked(null);
      qc.invalidateQueries({ queryKey: ['store', 'audits'] });
    },
  });
  const open = list.data?.open;

  if (list.isLoading) return <Spinner />;
  if (viewing) return <AuditReport auditId={viewing} onBack={() => setViewing(null)} />;
  if (open) return <CountSheets auditId={open.id} canRun={canRun} onCompleted={() => setViewing(open.id)} />;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rule bg-surface p-4">
        <div>
          <h2 className="text-sm font-medium text-ink">Count the shelves</h2>
          <p className="text-xs text-ink-3">
            Pick the days the audit covers. The sheets show what each shelf should have held at the end of the last day, and the report says what was bought, handed over and used in those days. Nothing changes until you complete it.
          </p>
        </div>
        {canRun && (
          <div className="flex flex-wrap items-center gap-2">
            <RangeInputs range={range} latest={today()} onRange={setPicked} />
            <Button onClick={() => start.mutate()} busy={start.isPending}>
              Start an audit
            </Button>
          </div>
        )}
      </div>
      {start.error && (
        <p role="alert" className="mb-3 text-sm text-bad">
          {start.error.message}
        </p>
      )}
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-3">Past audits</h3>
      <ul className="space-y-1.5 text-sm">
        {(list.data?.audits ?? [])
          .filter((a) => a.status !== 'open')
          .map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 rounded-lg border border-rule bg-surface px-3 py-2">
              <span>
                <span className="text-ink">{a.audit_number}</span>
                <span className="text-ink-3">
                  {' '}
                  · {a.status} · {periodLabel(a.period)} · {a.started_by_name}
                </span>
                {a.status === 'completed' && (
                  <div className="text-xs text-ink-3">
                    Used {formatPKR(a.used_value ?? 0)} · takings {formatPKR(a.sales_total ?? 0)} · expenses {formatPKR(a.expenses_total ?? 0)}
                  </div>
                )}
              </span>
              {a.status === 'completed' && (
                <Button size="sm" variant="secondary" onClick={() => setViewing(a.id)}>
                  Report
                </Button>
              )}
            </li>
          ))}
        {list.data && list.data.audits.length === 0 && <li className="text-ink-3">No audits yet.</li>}
      </ul>
    </div>
  );
}

/** The two sheets, side by side on a wide screen, one under the other on a narrow one. */
function CountSheets({ auditId, canRun, onCompleted }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['store', 'audit', auditId], queryFn: () => api.get(`/store/audits/${auditId}`) });
  const [shelf, setShelf] = useState('store');
  const [edits, setEdits] = useState({});
  const [term, setTerm] = useState('');

  const save = useMutation({
    mutationFn: (lines) => api.put(`/store/audits/${auditId}/lines`, { lines }),
    onSuccess: () => {
      setEdits({});
      qc.invalidateQueries({ queryKey: ['store', 'audit', auditId] });
    },
  });
  const complete = useMutation({
    mutationFn: async () => {
      const pending = Object.entries(edits).map(([lineId, countedPacks]) => ({ lineId, countedPacks }));
      if (pending.length) await api.put(`/store/audits/${auditId}/lines`, { lines: pending });
      return api.post(`/store/audits/${auditId}/complete`);
    },
    onSuccess: () => {
      onCompleted();
      qc.invalidateQueries({ queryKey: ['store'] });
    },
  });
  const cancel = useMutation({ mutationFn: () => api.post(`/store/audits/${auditId}/cancel`), onSuccess: () => qc.invalidateQueries({ queryKey: ['store'] }) });

  // Save what has been typed every few seconds, so a dropped connection or a closed laptop loses nothing.
  useEffect(() => {
    const pending = Object.entries(edits);
    if (!pending.length) return undefined;
    const t = setTimeout(() => save.mutate(pending.map(([lineId, countedPacks]) => ({ lineId, countedPacks }))), 2500);
    return () => clearTimeout(t);
  }, [edits]); // eslint-disable-line react-hooks/exhaustive-deps

  const lines = useMemo(() => {
    const all = data?.[shelf] ?? [];
    const needle = term.trim().toLowerCase();
    return needle ? all.filter((l) => l.name.toLowerCase().includes(needle)) : all;
  }, [data, shelf, term]);

  if (isLoading || !data) return <Spinner />;
  const counted = (l) => (edits[l.id] !== undefined ? edits[l.id] : l.countedPacks ?? '');
  const done = (key) => (data[key] ?? []).filter((l) => l.countedPacks != null || (edits[l.id] !== undefined && edits[l.id] !== '')).length;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-brand-400/50 bg-surface p-4">
        <div className="mr-auto">
          <h2 className="text-sm font-medium text-ink">
            {data.audit.audit_number} · {periodLabel(data.audit.period)}
          </h2>
          <p className="text-xs text-ink-3">
            Type what you see, in packs. Store {done('store')}/{data.store.length} · Kitchen {done('kitchen')}/{data.kitchen.length} counted. Blank lines are taken as the system's figure.
          </p>
        </div>
        {canRun && (
          <>
            <Button variant="ghost" onClick={() => cancel.mutate()} busy={cancel.isPending}>
              Cancel audit
            </Button>
            <Button onClick={() => complete.mutate()} busy={complete.isPending}>
              Complete audit
            </Button>
          </>
        )}
      </div>
      {complete.error && (
        <p role="alert" className="mb-3 text-sm text-bad">
          {complete.error.message}
        </p>
      )}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div role="tablist" className="flex rounded-lg border border-rule p-0.5">
          {['store', 'kitchen'].map((s) => (
            <button key={s} role="tab" aria-selected={shelf === s} onClick={() => setShelf(s)} className={`rounded-md px-3 py-1.5 text-sm font-medium ${shelf === s ? 'bg-brand-500 text-black' : 'text-ink-2'}`}>
              {s === 'store' ? 'Store shelf' : 'Kitchen shelf'}
            </button>
          ))}
        </div>
        <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Find an item" aria-label="Find an item" className="h-10 w-56 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink" />
        <span className="ml-auto text-xs text-ink-3">{save.isPending ? 'Saving…' : Object.keys(edits).length ? 'Unsaved' : 'Saved'}</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-rule">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-ink-3">
              <th className="p-3 font-medium">Item</th>
              <th className="p-3 font-medium">Pack</th>
              <th className="p-3 text-right font-medium">System says</th>
              <th className="p-3 text-right font-medium">Counted (packs)</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id} className="border-t border-rule">
                <td className="p-3 text-ink">
                  {l.name}
                  <div className="text-xs text-ink-3">{l.category}</div>
                </td>
                <td className="p-3 text-ink-2">
                  {l.pack_name || l.unit}
                  {l.packSize !== 1 && <span className="text-ink-3"> = {packs(l.packSize)} {l.unit}</span>}
                </td>
                <td className="p-3 text-right tabular-nums text-ink-3">{packs(l.systemPacks)}</td>
                <td className="p-3 text-right">
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={counted(l)}
                    disabled={!canRun}
                    onChange={(e) => setEdits({ ...edits, [l.id]: e.target.value })}
                    aria-label={`Counted packs of ${l.name} in the ${shelf}`}
                    className={`h-10 w-24 rounded-lg border bg-surface-3 px-2 text-right text-sm text-ink ${counted(l) !== '' && Number(counted(l)) !== l.systemPacks ? 'border-warn' : 'border-rule'}`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditReport({ auditId, onBack }) {
  const { data, isLoading, error } = useQuery({ queryKey: ['store', 'audit-report', auditId], queryFn: () => api.get(`/store/audits/${auditId}/report`) });
  if (isLoading) return <Spinner />;
  if (error)
    return (
      <p role="alert" className="text-sm text-bad">
        {error.message}
      </p>
    );
  return <ReportView report={data} onBack={onBack} />;
}

/** The report on screen and on paper (A4, via the statement print rule). */
export function ReportView({ report, onBack }) {
  const t = report.totals;
  const [onlyUsed, setOnlyUsed] = useState(true);
  const rows = onlyUsed ? report.items.filter((r) => r.kitchen.used || r.store.difference) : report.items;
  const Row = ({ label, value, strong }) => (
    <div className={`flex justify-between gap-3 ${strong ? 'font-semibold text-ink' : 'text-ink-2'}`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
  return (
    <div>
      <div className="no-print mb-3 flex flex-wrap items-center gap-2">
        <Button variant="secondary" size="sm" onClick={onBack}>
          ← Back
        </Button>
        <label className="ml-2 flex items-center gap-2 text-sm text-ink-2">
          <input type="checkbox" checked={onlyUsed} onChange={(e) => setOnlyUsed(e.target.checked)} />
          Only items that moved
        </label>
        <div className="ml-auto flex items-center gap-2">
          <ReportExport path={`/store/audits/${report.audit.id}/report`} name={`Audit ${report.audit.audit_number}`} />
          <Button size="sm" onClick={() => window.print()}>
            Print
          </Button>
        </div>
      </div>
      <div className="statement rounded-xl border border-rule bg-surface p-4">
        <h2 className="text-lg font-semibold text-ink">
          Audit {report.audit.audit_number} · {periodLabel(report.period)}
        </h2>
        <p className="mb-4 text-xs text-ink-3">
          Completed by {report.audit.completed_by_name || '—'} on {when(report.audit.completed_at)}. Values at the average cost of the packs on each shelf.
        </p>

        <div className="mb-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-rule p-3 text-sm">
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-3">Stock</h3>
            <div className="space-y-1">
              <Row label="Bought in this period" value={formatPKR(t.purchases)} />
              <Row label="Handed to the kitchen" value={formatPKR(t.issued)} />
              <Row label="Used in the kitchen" value={formatPKR(t.used)} strong />
              <Row label="Store difference (counted − expected)" value={formatPKR(t.storeDifference)} />
              <Row label="Left in the store" value={formatPKR(t.storeLeft)} />
              <Row label="Left in the kitchen" value={formatPKR(t.kitchenLeft)} />
            </div>
          </div>
          <div className="rounded-lg border border-rule p-3 text-sm">
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-3">Approximate profit</h3>
            <div className="space-y-1">
              <Row label={`Takings (${t.orders} bills)`} value={formatPKR(t.sales)} />
              <Row label="− Stock used" value={`− ${formatPKR(t.used)}`} />
              <Row label="= Gross profit" value={formatPKR(t.grossProfit)} strong />
              <Row label={`− Expenses entered (${t.expenseCount})`} value={`− ${formatPKR(t.expenses)}`} />
              <Row label="= Profit" value={formatPKR(t.profit)} strong />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-center text-xs text-ink-3">
                <th className="p-2"></th>
                <th colSpan={6} className="border-b border-rule p-1 font-medium uppercase tracking-wide">
                  Store shelf
                </th>
                <th colSpan={6} className="border-b border-l border-rule p-1 font-medium uppercase tracking-wide">
                  Kitchen shelf
                </th>
              </tr>
              <tr className="text-left text-ink-3">
                <th className="p-2 font-medium">Item</th>
                <th className="p-2 text-right font-medium">Start</th>
                <th className="p-2 text-right font-medium">Bought</th>
                <th className="p-2 text-right font-medium">To kitchen</th>
                <th className="p-2 text-right font-medium">Expected</th>
                <th className="p-2 text-right font-medium text-ink">Counted (Store)</th>
                <th className="p-2 text-right font-medium">Difference</th>
                <th className="border-l border-rule p-2 text-right font-medium">Start</th>
                <th className="p-2 text-right font-medium">Received</th>
                <th className="p-2 text-right font-medium">Expected</th>
                <th className="p-2 text-right font-medium text-ink">Counted (Kitchen)</th>
                <th className="p-2 text-right font-medium">Used</th>
                <th className="p-2 text-right font-medium">Used Rs</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const p = (v) => packs(Math.round((v / r.packSize) * 100) / 100);
                const kitchenExpected = r.kitchen.expected ?? r.kitchen.opening + r.kitchen.received - r.kitchen.returned;
                return (
                  <tr key={r.id} className="border-t border-rule">
                    <td className="p-2 text-ink">
                      {r.name} <span className="text-ink-3">· {r.packName || r.unit}</span>
                    </td>
                    <td className="p-2 text-right tabular-nums text-ink-2">{p(r.store.opening)}</td>
                    <td className="p-2 text-right tabular-nums text-ink-2">{p(r.store.purchases)}</td>
                    <td className="p-2 text-right tabular-nums text-ink-2">{p(r.store.issued)}</td>
                    <td className="p-2 text-right tabular-nums text-ink-2">{p(r.store.expected)}</td>
                    <td className="p-2 text-right tabular-nums font-medium text-ink">{p(r.store.counted)}</td>
                    <td className={`p-2 text-right tabular-nums ${r.store.difference ? 'text-warn' : 'text-ink-3'}`}>{r.store.difference ? p(r.store.difference) : '—'}</td>
                    <td className="border-l border-rule p-2 text-right tabular-nums text-ink-2">{p(r.kitchen.opening)}</td>
                    <td className="p-2 text-right tabular-nums text-ink-2">{p(r.kitchen.received)}</td>
                    <td className="p-2 text-right tabular-nums text-ink-2">{p(kitchenExpected)}</td>
                    <td className="p-2 text-right tabular-nums font-medium text-ink">{p(r.kitchen.counted)}</td>
                    <td className="p-2 text-right tabular-nums text-ink">{p(r.kitchen.used)}</td>
                    <td className="p-2 text-right tabular-nums text-ink">{formatPKR(r.kitchen.usedValue)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-ink-3">Quantities in packs. {report.note}</p>
      </div>
    </div>
  );
}
